import React, { useEffect, useState, useRef } from "react";
import SignaturePad from "signature_pad";
import {
  PencilIcon,
  ChatBubbleLeftIcon,
  PhotoIcon,
  DocumentTextIcon,
  StarIcon,
  TrashIcon
} from "@heroicons/react/24/solid";
import axios from "axios";
import { toast } from "react-toastify";
import {
  getSignature,
  deleteSignature
} from "../../Constants/apiRoutes";
import { useConfirmation } from "../../Components/ConfirmDialog";
import { ToastContainer } from "react-toastify";
const SignatureDialog = ({
  showDialog,
  setShowDialog,
  onSaveSignature,
  documentId,
}) => {
  const [activeTab, setActiveTab] = useState("create"); // 'create' or 'saved'
  const [signatureType, setSignatureType] = useState("draw");
  const [textSignature, setTextSignature] = useState("");
  const [fontSize, setFontSize] = useState(24);
  const [fontColor, setFontColor] = useState("#000000");
  const [signatureFont, setSignatureFont] = useState("cursive");
  const [signatureImage, setSignatureImage] = useState(null);
  const [documentSignatures, setDocumentSignatures] = useState([]);
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [savingSignature, setSavingSignature] = useState(false);

  const { showConfirmation, ConfirmationDialog } = useConfirmation();
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (showDialog) {
      fetchSignatures();
    }
  }, [showDialog, documentId]);

  // Set initial active tab based on whether we have saved signatures
  useEffect(() => {
    if (documentSignatures.length > 0) {
      setActiveTab("saved");
    }
  }, [documentSignatures]);

  useEffect(() => {
    if (showDialog && signatureType === "draw" && activeTab === "create" && canvasRef.current) {
      const canvas = canvasRef.current;
      const signaturePad = new SignaturePad(canvas, {
        backgroundColor: "rgba(255, 255, 255, 0)",
        penColor: "rgb(0, 0, 0)",
        minWidth: 0.5,
        maxWidth: 2.5,
        throttle: 16,
      });
      signaturePadRef.current = signaturePad;

      const resizeCanvas = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext("2d").scale(ratio, ratio);
        signaturePad.clear();
      };

      window.addEventListener("resize", resizeCanvas);
      resizeCanvas();

      return () => {
        window.removeEventListener("resize", resizeCanvas);
        if (signaturePadRef.current) {
          signaturePadRef.current.off();
        }
      };
    }
  }, [showDialog, signatureType, activeTab]);

  const fetchSignatures = async () => {
    setLoadingSignatures(true);
    try {
      // Extract external user ID from the URL, if present
      const pathnameParts = window.location.pathname.split("/");
      const externalUserId = pathnameParts.includes("public-access")
        ? Number(pathnameParts[pathnameParts.indexOf("public-access") + 1])
        : null;
  
      const userId = externalUserId || Number(localStorage.getItem("UserID"));
  
      const response = await axios.get(
        `${getSignature}/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "arraybuffer", // important: get raw binary
        }
      );
  
      const base64 = btoa(
        new Uint8Array(response.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );
  
      const imageSrc = `data:image/png;base64,${base64}`;
      setDocumentSignatures([{ id: 1, src: imageSrc }]);
    } catch (err) {
      console.error("Error fetching signatures:", err);
      if (err.response && err.response.status !== 404) {
        toast.error("Failed to fetch signatures.");
      }
      setDocumentSignatures([]);
    } finally {
      setLoadingSignatures(false);
    }
  };
  
  const handleDeleteSignature = (signatureId) => {
    showConfirmation(
      "Delete Signature",
      "Are you sure you want to delete this signature? This action cannot be undone.",
      async () => {
        try {
          const userId = Number(localStorage.getItem("UserID"));
          await axios.delete(`${deleteSignature}/${userId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          fetchSignatures(); // Refresh signatures after deletion
        } catch (err) {
          console.error("Error deleting signature:", err);
          throw new Error("Failed to delete signature");
        }
      }
    );
  };

  const handleClearSignature = () => {
    if (signatureType === "draw" && signaturePadRef.current) {
      signaturePadRef.current.clear();
    } else if (signatureType === "text") {
      setTextSignature("");
    } else if (signatureType === "image") {
      setSignatureImage(null);
    }
  };

  const handleImageUpload = (e) => {
  const file = e.target.files[0];
  
  if (!file) return;

  // Check file type
  const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  if (!validTypes.includes(file.type)) {
    toast.error('Please upload only .png or .jpg or .jpeg images');
    return;
  }

  const maxSize = 2 * 1024 * 1024; // 2MB
  if (file.size > maxSize) {
    toast.error('Image size should be less than 2MB');
    return;
  }

  const reader = new FileReader();
  reader.onload = (event) => {
    setSignatureImage(event.target.result);
  };
  reader.onerror = () => {
    toast.error('Error reading the file');
  };
  reader.readAsDataURL(file);
};

  const textToImage = (text, fontFamily, fontSize, color) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    // Set canvas size based on text dimensions
    context.font = `${fontSize}px ${fontFamily}`;
    const textWidth = context.measureText(text).width;
    const textHeight = fontSize * 1.2; // Approximate height
    
    canvas.width = textWidth + 20; // Add some padding
    canvas.height = textHeight + 20;
    
    // Redraw text with proper settings
    context.font = `${fontSize}px ${fontFamily}`;
    context.fillStyle = color;
    context.textBaseline = 'middle';
    context.fillText(text, 10, canvas.height / 2);
    
    return canvas.toDataURL('image/png');
  };

  const saveSignature = async () => {
    let signatureData = null;
  
    if (activeTab === "saved" && selectedSignature) {
      signatureData = {
        type: "image",
        data: selectedSignature.src,
      };
      onSaveSignature(signatureData);
      setShowDialog(false);
      return;
    }
  
    if (signatureType === "draw" && signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      signatureData = {
        type: "image", // Changed from "draw" to "image" for consistency
        data: signaturePadRef.current.toDataURL(),
      };
    } else if (signatureType === "text" && textSignature.trim()) {
      // Convert text to image
      const imageData = textToImage(
        textSignature,
        signatureFont,
        fontSize,
        fontColor
      );
      signatureData = {
        type: "image",
        data: imageData,
      };
    } else if (signatureType === "image" && signatureImage) {
      signatureData = {
        type: "image",
        data: signatureImage,
      };
    }
  
    if (signatureData) {
      setSavingSignature(true);
      try {
        const newSignature = {
          id: Date.now(),
          src: signatureData.data
        };
    
        setDocumentSignatures(prev => [...prev, newSignature]);
        toast.success("Signature saved successfully");
        
        // Pass to parent component
        onSaveSignature(signatureData);
        setShowDialog(false);
      } catch (error) {
        console.error("Error saving signature:", error);
        toast.error("Failed to save signature");
      } finally {
        setSavingSignature(false);
      }
    } else {
      toast.warning("Please create or select a signature first");
    }
  };
  
 
  const selectSignature = (signature) => {
    setSelectedSignature(selectedSignature?.id === signature.id ? null : signature);
  };

  return (
    showDialog && (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
           <ConfirmationDialog />
           <ToastContainer/>
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg">
          <div className="flex border-b mb-4">
            <button
              className={`py-2 px-4 font-medium ${
                activeTab === "create"
                  ? "text-[#8B4513] border-b-2 border-[#8B4513]"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("create")}
            >
              Create Signature
            </button>
            <button
              className={`py-2 px-4 font-medium ${
                activeTab === "saved"
                  ? "text-[#8B4513] border-b-2 border-[#8B4513]"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("saved")}
            >
              Saved Signatures
            </button>
          </div>

          {activeTab === "create" ? (
            <>
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Signature Type
              </h3>
              <div className="flex gap-2 mb-4">
                {["draw", "text", "image"].map((type) => (
                  <label
                    key={type}
                    className={`flex-1 cursor-pointer p-3 border rounded-md transition-all ${
                      signatureType === type
                        ? "border-blue-500 bg-blue-100"
                        : "border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="signatureType"
                      value={type}
                      checked={signatureType === type}
                      onChange={() => setSignatureType(type)}
                      className="hidden"
                    />
                    <div className="flex flex-col items-center">
                      <div className="text-2xl mb-1">
                        {type === "draw" ? (
                          <PencilIcon className="w-6 h-6 text-gray-700" />
                        ) : type === "text" ? (
                          <ChatBubbleLeftIcon className="w-6 h-6 text-gray-700" />
                        ) : (
                          <PhotoIcon className="w-6 h-6 text-gray-700" />
                        )}
                      </div>
                      <span className="capitalize">{type}</span>
                    </div>
                  </label>
                ))}
              </div>

              <div className="space-y-4">
                {signatureType === "draw" && (
                  <div className="border p-3 rounded-md">
                    <canvas
                      ref={canvasRef}
                      className="w-full h-40 border"
                    ></canvas>
                  </div>
                )}

                {signatureType === "text" && (
                  <div>
                    <input
                      type="text"
                      value={textSignature}
                      onChange={(e) => setTextSignature(e.target.value)}
                      placeholder="Enter your signature"
                      className="w-full p-2 border rounded-md mb-4"
                      style={{
                        fontFamily: signatureFont,
                        fontSize: `${fontSize}px`,
                        color: fontColor,
                      }}
                    />
                  
                  </div>
                )}

                {signatureType === "image" && (
                  <div className="flex flex-col items-center">
                    {signatureImage ? (
                      <img
                        src={signatureImage}
                        alt="Signature"
                        className="max-h-40 border rounded-md mb-4"
                      />
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-md p-8 text-center mb-4">
                        <PhotoIcon className="w-12 h-12 mx-auto text-gray-400" />
                        <p className="mt-2 text-sm text-gray-600">
                          No image selected
                        </p>
                      </div>
                    )}
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                    >
                      {signatureImage ? "Change Image" : "Upload Signature Image"}
                    </button>
                  
                    <input
  type="file"
  ref={fileInputRef}
  onChange={handleImageUpload}
  accept=".png,.jpg,.jpeg,image/png,image/jpg,image/jpeg"
  className="hidden"
/>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6 gap-2">
                <button
                  onClick={() => setShowDialog(false)}
                  className="px-4 py-2 bg-[#8B4513] text-white rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearSignature}
                  className="px-4 py-2 bg-[#8B4513] rounded-md text-white"
                >
                  Clear
                </button>
                <button
                  onClick={saveSignature}
                  className="px-4 py-2 bg-[#8B4513] text-white rounded-md"
                >
                  Save
                </button>
              </div>
            </>
          ) : (
            <div className="min-h-[300px] flex flex-col">
              {loadingSignatures ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : documentSignatures.length > 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className="mb-4 grid grid-cols-2 gap-4 w-full">
                    {documentSignatures.map((signature) => (
                      <div 
                        key={signature.id}
                        className={`relative border rounded p-2 cursor-pointer ${
                          selectedSignature?.id === signature.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                        }`}
                        onClick={() => selectSignature(signature)}
                      >
                        <img
                          src={signature.src}
                          alt="Signature"
                          className="w-full h-16 object-contain rounded"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData("signatureType", "image");
                            e.dataTransfer.setData("signatureSrc", signature.src);
                          }}
                        />
                        {selectedSignature?.id === signature.id && (
                          <StarIcon className="absolute top-1 right-1 w-4 h-4 text-yellow-500" />
                        )}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSignature(signature.id);
                          }}
                          className="absolute bottom-1 right-1 text-red-500 hover:text-red-700"
                          title="Delete signature"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Click to select a signature or drag and drop onto the document
                  </p>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <DocumentTextIcon className="w-12 h-12 text-gray-400 mb-2" />
                  <p className="text-gray-600">No saved signatures found</p>
                  <button
                    onClick={() => setActiveTab("create")}
                    className="mt-4 text-[#8B4513]"
                  >
                    Create a new signature
                  </button>
                </div>
              )}
              <div className="flex justify-end mt-6 gap-2">
                <button
                  onClick={() => setShowDialog(false)}
                  className="px-4 py-2 bg-[#8B4513] text-white rounded-md"
                >
                  Cancel
                </button>
                {selectedSignature && (
                  <button
                    onClick={saveSignature}
                    className="px-4 py-2 bg-[#8B4513] text-white rounded-md"
                    disabled={savingSignature}
                  >
                    {savingSignature ? "Saving..." : "Use Selected"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  );
};

export default SignatureDialog;